import { useState, useMemo, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { operatorApi } from '../services/operatorApi'
import OperatorManagement from './OperatorManagement'
import UserProfile from './UserProfile'
import LoadingSpinner from './common/LoadingSpinner'
import ErrorState from './common/ErrorState'
import Pagination from './common/Pagination'
import { PAGINATION_DEFAULTS } from '../config/api.config'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDaysAgo(days) {
  // Handle negative values (future dates) or invalid values
  if (days == null || isNaN(days) || days < 0) {
    return 'Today'
  }
  
  // Round to whole number
  const roundedDays = Math.round(days)
  
  if (roundedDays === 0) return 'Today'
  if (roundedDays === 1) return 'Yesterday'
  if (roundedDays < 7) return `${roundedDays}d ago`
  if (roundedDays < 30) {
    const weeks = Math.round(roundedDays / 7)
    return `${weeks}w ago`
  }
  if (roundedDays < 365) {
    const months = Math.round(roundedDays / 30)
    return `${months}mo ago`
  }
  const years = Math.round(roundedDays / 365)
  return `${years}y ago`
}

function trialDaysLeftFor(c) {
  if (c?.subscriptionState !== 'trial') return null
  if (c.trialDaysLeft != null && c.trialDaysLeft !== '') {
    const n = Math.round(Number(c.trialDaysLeft))
    return Number.isFinite(n) ? n : null
  }
  if (c.crmSource !== 'ghl') return null
  const installed = parseApiDate(c.installedAt)
  if (!installed) return null
  const ends = new Date(Date.UTC(installed.getUTCFullYear(), installed.getUTCMonth(), installed.getUTCDate() + 14))
  const today = new Date()
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  return Math.max(0, Math.round((ends.getTime() - todayUtc) / 86400000))
}

function parseApiDate(value) {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatInstalledDate(installedAt, daysAgo) {
  const installed = parseApiDate(installedAt)
  if (installed) {
    return installed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  // Backward-compatible fallback for older API payloads.
  const n = Number.isFinite(Number(daysAgo)) ? Math.max(0, Math.round(Number(daysAgo))) : 0
  const fallback = new Date()
  fallback.setDate(fallback.getDate() - n)
  return fallback.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatLastActive(lastActiveAt, daysAgo) {
  const lastActive = parseApiDate(lastActiveAt)
  if (!lastActive) {
    return formatDaysAgo(daysAgo)
  }
  const ms = Date.now() - lastActive.getTime()
  if (ms <= 0) return 'Today'
  const day = 24 * 60 * 60 * 1000
  return formatDaysAgo(ms / day)
}

function matchPct(stats) {
  if (!stats.total) return 0
  return Math.round((stats.matched / stats.total) * 100)
}

// ── Config ────────────────────────────────────────────────────────────────────

const STATE_CONFIG = {
  active:        { label: 'Active',         bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-400'  },
  trial:         { label: 'Trial',          bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-400'   },
  trial_expired: { label: 'Trial Expired',  bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  payment_failed:{ label: 'Payment Failed', bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-400' },
  suspended:     { label: 'Suspended',      bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-400'    },
  cancelled:     { label: 'Cancelled',      bg: 'bg-gray-100',   text: 'text-gray-500',   dot: 'bg-gray-300'   },
}

const ONBOARDING_STEPS = [
  { key: 'installed',           label: 'App Installed'      },
  { key: 'profile_saved',       label: 'Basic Info Entered' },
  { key: 'stripe_connected',    label: 'Payment Connected'  }, // display label resolved per-org provider
  { key: 'agents_assigned',     label: 'Agents Assigned'    },
  { key: 'matching_complete',   label: 'Auto-Match Run'     },
  { key: 'onboarding_complete', label: 'Setup Complete'     },
]

const STAGE_ORDER = ONBOARDING_STEPS.map(s => s.key)

const ONBOARDING_LABEL = {
  installed:           'Installed',
  profile_saved:       'Profile Saved',
  stripe_connected:    'Payment Connected',
  agents_assigned:     'Agents Assigned',
  matching_complete:   'Matching Done',
  onboarding_complete: 'Setup Complete',
}

const PROVIDER_DISPLAY_NAMES = {
  stripe: 'Stripe',
  adyen: 'Adyen',
  paypal: 'PayPal',
  braintree: 'Braintree',
}

function paymentConnectionFor(customer) {
  return customer?.paymentConnection || customer?.stripeConnection || { connected: false, mode: null }
}

function crmLabel(customer) {
  if (customer?.crmSourceLabel) return customer.crmSourceLabel
  if (customer?.crmSource === 'ghl') return 'GoHighLevel'
  if (customer?.crmSource === 'pipedrive') return 'Pipedrive'
  return 'Salesforce'
}

function crmBadgeClass(source) {
  if (source === 'ghl') return 'bg-orange-100 text-orange-700'
  if (source === 'pipedrive') return 'bg-green-100 text-green-700'
  return 'bg-sky-100 text-sky-700'
}

function showsTestModeBadge(customer) {
  if (!customer || customer.crmSource === 'ghl' || customer.hideTestModeWarning) return false
  const connection = paymentConnectionFor(customer)
  return connection.connected && connection.mode === 'test'
}

function titleCaseProvider(code) {
  const normalized = String(code || '').trim().toLowerCase()
  if (!normalized) return 'Payment'
  return PROVIDER_DISPLAY_NAMES[normalized]
    || (normalized.charAt(0).toUpperCase() + normalized.slice(1))
}

/** Resolve display name for the org's payment provider (never hardcode Stripe). */
function resolveProviderName(customer) {
  const labels = customer?.providerLabels || customer?.provider_labels || {}
  if (labels.provider_display_name) return labels.provider_display_name
  if (customer?.paymentProviderName) return customer.paymentProviderName
  if (customer?.payment_provider_name) return customer.payment_provider_name
  const connection = paymentConnectionFor(customer)
  if (connection?.providerName) return connection.providerName
  return titleCaseProvider(customer?.paymentProvider || customer?.payment_provider || connection?.provider)
}

function connectionSectionTitle(customer) {
  const labels = customer?.providerLabels || customer?.provider_labels || {}
  return labels.operator_connection_section_title
    || `${resolveProviderName(customer)} Connection`
}

function paymentConnectedStepLabel(customer) {
  const labels = customer?.providerLabels || customer?.provider_labels || {}
  return labels.operator_onboarding_payment_connected
    || `${resolveProviderName(customer)} Connected`
}

function normalizeOperatorCustomer(raw) {
  if (!raw || typeof raw !== 'object') return raw
  const paymentProvider = raw.paymentProvider || raw.payment_provider || raw.paymentConnection?.provider || 'stripe'
  const providerLabels = raw.providerLabels || raw.provider_labels || {}
  const paymentProviderName = raw.paymentProviderName
    || raw.payment_provider_name
    || providerLabels.provider_display_name
    || titleCaseProvider(paymentProvider)
  const paymentConnection = raw.paymentConnection || raw.stripeConnection || { connected: false, mode: null }
  return {
    ...raw,
    paymentProvider,
    paymentProviderName,
    providerLabels,
    paymentConnection: {
      ...paymentConnection,
      provider: paymentConnection.provider || paymentProvider,
      providerName: paymentConnection.providerName || paymentProviderName,
    },
  }
}

const BILLING_EVENT_STYLE = {
  success: 'text-green-600',
  info:    'text-blue-600',
  warning: 'text-amber-600',
  error:   'text-red-600',
}

function isAtRisk(state) {
  return ['trial_expired', 'payment_failed', 'suspended'].includes(state)
}

// ── Small reusable pieces ─────────────────────────────────────────────────────

function StateBadge({ state, size = 'sm' }) {
  const cfg = STATE_CONFIG[state] || STATE_CONFIG.active
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.text} ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  )
}

function onboardingStageLabel(stage, customer) {
  if (stage === 'stripe_connected') {
    return paymentConnectedStepLabel(customer)
  }
  return ONBOARDING_LABEL[stage] || stage
}

function OnboardingBadge({ stage, customer }) {
  const isDone = stage === 'onboarding_complete'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      isDone ? 'bg-green-50 text-green-700' : 'bg-indigo-50 text-indigo-700'
    }`}>
      {isDone
        ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
      }
      {onboardingStageLabel(stage, customer)}
    </span>
  )
}

function SectionLabel({ children }) {
  return <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{children}</p>
}

function Divider() {
  return <div className="border-t border-gray-100 my-4" />
}

// ── Metric cards ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub, accent, alert }) {
  const accentMap = {
    green:  { num: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-100' },
    blue:   { num: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-100'  },
    indigo: { num: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-100'},
    red:    { num: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-100'   },
  }
  const a = accentMap[accent] || accentMap.blue
  return (
    <div className={`bg-white rounded-xl border p-4 ${alert ? `${a.border} ${a.bg}` : 'border-gray-200'}`}>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${alert ? a.num : 'text-gray-900'}`}>{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
    </div>
  )
}

// ── Filter tabs ───────────────────────────────────────────────────────────────

const FILTERS = [
  { id: 'all',            label: 'All'            },
  { id: 'trial',          label: 'Trial'          },
  { id: 'active',         label: 'Active'         },
  { id: 'at_risk',        label: 'At Risk'        },
  { id: 'suspended',      label: 'Suspended'      },
  { id: 'cancelled',      label: 'Cancelled'      },
]

function FilterTabs({ filter, onChange, counts }) {
  function count(id) {
    return counts[id] || 0
  }

  return (
    <div className="flex items-center gap-1">
      {FILTERS.map(f => {
        const n = count(f.id)
        const isActive = filter === f.id
        return (
          <button
            key={f.id}
            onClick={() => onChange(f.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
              isActive
                ? 'bg-indigo-600 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {f.label}
            {n > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                isActive ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-500'
              }`}>{n}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ── Sortable column header ────────────────────────────────────────────────────

function SortTh({ label, field, sortBy, sortDir, onSort, className = '' }) {
  const active = sortBy === field
  return (
    <th
      onClick={() => onSort(field)}
      className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide cursor-pointer select-none transition-colors ${
        active ? 'text-indigo-600' : 'text-gray-500 hover:text-gray-800'
      } ${className}`}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className={`transition-opacity ${active ? 'opacity-100' : 'opacity-0'}`}>
          {active && sortDir === 'asc'
            ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
            : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          }
        </span>
      </div>
    </th>
  )
}

// ── Customer table ────────────────────────────────────────────────────────────

function CustomerTable({ customers, selectedId, onSelect, sortBy, sortDir, onSort }) {
  if (customers.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
        <p className="text-sm text-gray-400">No customers match this filter.</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50">
            <SortTh label="Organization" field="orgName"    sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">CRM</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Admin</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Onboarding</th>
            <SortTh label="Status"       field="status"     sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <SortTh label="Seats / MRR"  field="mrr"        sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <SortTh label="Installed"    field="installed"  sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <SortTh label="Last Active"  field="lastActive" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {customers.map(c => (
            <CustomerRow
              key={c.id}
              customer={c}
              isSelected={selectedId === c.id}
              onClick={() => onSelect(selectedId === c.id ? null : c.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CustomerRow({ customer: c, isSelected, onClick }) {
  const hasTestMode = showsTestModeBadge(c)
  const trialDaysLeft = trialDaysLeftFor(c)
  const isStuck = c.onboardingStage !== 'onboarding_complete'
  const risk = isAtRisk(c.subscriptionState)

  return (
    <tr
      onClick={onClick}
      className={`cursor-pointer transition-colors ${
        isSelected
          ? 'bg-indigo-50'
          : risk
          ? 'bg-red-50/30 hover:bg-red-50/60'
          : 'hover:bg-gray-50'
      }`}
    >
      {/* Organization */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold ${
            isSelected ? 'bg-indigo-200 text-indigo-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {c.orgName.charAt(0)}
          </div>
          <div>
            <div className="font-medium text-gray-900">{c.orgName}</div>
            <div className="text-xs text-gray-400 font-mono">{c.orgId}</div>
          </div>
        </div>
      </td>

      {/* CRM source */}
      <td className="px-4 py-3">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${crmBadgeClass(c.crmSource)}`}>
          {crmLabel(c)}
        </span>
      </td>

      {/* Admin */}
      <td className="px-4 py-3">
        <div className="font-medium text-gray-800">{c.admin.name}</div>
        <div className="text-xs text-gray-400">{c.admin.email}</div>
      </td>

      {/* Onboarding */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <OnboardingBadge stage={c.onboardingStage} customer={c} />
          {isStuck && (
            <span title="Setup incomplete" className="text-amber-500">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </span>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StateBadge state={c.subscriptionState} />
          {c.subscriptionState === 'trial' && trialDaysLeft != null && (
            <span className={`text-xs font-medium ${trialDaysLeft <= 3 ? 'text-orange-600' : 'text-gray-400'}`}>
              {trialDaysLeft}d left
            </span>
          )}
          {c.subscriptionState === 'payment_failed' && c.graceDaysLeft != null && (
            <span className="text-xs font-medium text-orange-600">{c.graceDaysLeft}d grace</span>
          )}
          {hasTestMode && (
            <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-semibold">TEST</span>
          )}
        </div>
      </td>

      {/* Seats / MRR */}
      <td className="px-4 py-3">
        {c.seats > 0 ? (
          <div className="text-sm text-gray-800">
            <span className="font-semibold">{c.seats} seats</span>
            {' / '}
            {c.mrr > 0 ? (
              <span className="text-gray-700">${c.mrr}/mo</span>
            ) : (
              <span className="text-amber-600 font-medium">$0/mo</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>

      {/* Installed */}
      <td className="px-4 py-3 text-xs text-gray-500">
        {formatInstalledDate(c.installedAt, c.installedDaysAgo)}
      </td>

      {/* Last Active */}
      <td className="px-4 py-3 text-xs text-gray-500">
        {formatLastActive(c.lastActiveAt, c.lastActiveDaysAgo)}
      </td>
    </tr>
  )
}

// ── Detail drawer ─────────────────────────────────────────────────────────────

function CustomerDrawer({ customer: c, notes, onNotesChange, onSaveNote, onClose, onExtendTrial, onMarkActive, onReset }) {
  const [extendMode, setExtendMode] = useState(false)
  const [extendDays, setExtendDays] = useState(7)
  const [confirmActivate, setConfirmActivate] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [noteSaved, setNoteSaved] = useState(false)
  const isGhl = c.crmSource === 'ghl'
  const isPipedrive = c.crmSource === 'pipedrive'

  const connection = paymentConnectionFor(c)
  const providerLabels = c.providerLabels || {}
  const providerName = resolveProviderName(c)
  const hasTestMode = showsTestModeBadge(c)
  const stageIndex = STAGE_ORDER.indexOf(c.onboardingStage)

  function handleExtend() {
    onExtendTrial(extendDays)
    setExtendMode(false)
  }

  function handleActivate() {
    onMarkActive()
    setConfirmActivate(false)
  }

  async function handleReset() {
    setResetting(true)
    try {
      await onReset()
      setConfirmReset(false)
    } catch {
      // Toast is shown by operatorApi.
    } finally {
      setResetting(false)
    }
  }

  function handleSaveNote() {
    onSaveNote()
    setNoteSaved(true)
    setTimeout(() => setNoteSaved(false), 2000)
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-[440px] bg-white shadow-2xl z-50 flex flex-col">

        {/* Drawer header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0 text-sm font-bold text-indigo-700">
              {c.orgName.charAt(0)}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{c.orgName}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <StateBadge state={c.subscriptionState} />
                {hasTestMode && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-semibold">TEST MODE</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">

          {/* Test mode warning */}
          {hasTestMode && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-start gap-2.5 mb-3">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <div>
                <p className="text-xs font-semibold text-yellow-800">
                  {providerLabels.operator_test_mode_title || `${providerName} test mode connected`}
                </p>
                <p className="text-xs text-yellow-700 mt-0.5">
                  {providerLabels.operator_test_mode_message ||
                    `This org is using test ${providerName} credentials. They won't see real payment data until live credentials are configured.`}
                </p>
              </div>
            </div>
          )}

          {/* Admin contact */}
          <SectionLabel>Admin Contact</SectionLabel>
          <div className="bg-gray-50 rounded-xl p-3 space-y-2 mb-2">
            <InfoRow icon="user" label="Name" value={c.admin.name} />
            <InfoRow icon="mail" label="Email" value={c.admin.email} mono />
            {c.admin.phone
              ? <InfoRow icon="phone" label="Phone" value={c.admin.phone} />
              : <InfoRow icon="phone" label="Phone" value="Not provided" muted />
            }
            <InfoRow icon="hash" label="Org ID" value={c.orgId} mono />
            <InfoRow icon="hash" label="CRM" value={crmLabel(c)} />
          </div>

          <Divider />

          {/* Payment provider connection */}
          <SectionLabel>{connectionSectionTitle(c)}</SectionLabel>
          {connection.connected ? (
            <div className="bg-gray-50 rounded-xl p-3 space-y-2 mb-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Status</span>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  <span className="text-xs font-medium text-green-700">Connected</span>
                </div>
              </div>
              <InfoRow label="Account" value={connection.accountName} />
              <InfoRow label="Account ID" value={connection.accountId} mono />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Mode</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                  connection.mode === 'live'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {connection.mode === 'live' ? 'LIVE' : 'TEST'}
                </span>
              </div>
              {connection.lastSynced && (
                <InfoRow label="Last synced" value={connection.lastSynced} muted />
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2 mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
              <span className="text-xs text-gray-400">Not connected</span>
            </div>
          )}

          <Divider />

          {/* Subscription */}
          <SectionLabel>Subscription</SectionLabel>
          <div className="bg-gray-50 rounded-xl p-3 space-y-2 mb-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">State</span>
              <StateBadge state={c.subscriptionState} />
            </div>
            {c.subscriptionState === 'trial' && trialDaysLeftFor(c) != null && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Trial remaining</span>
                <span className={`text-xs font-semibold ${trialDaysLeftFor(c) <= 3 ? 'text-orange-600' : 'text-gray-800'}`}>
                  {trialDaysLeftFor(c)} days
                </span>
              </div>
            )}
            {c.subscriptionState === 'payment_failed' && c.graceDaysLeft != null && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Grace period</span>
                <span className="text-xs font-semibold text-orange-600">{c.graceDaysLeft} days left</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Seats</span>
              <span className="text-xs font-semibold text-gray-800">{c.seats > 0 ? c.seats : '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">MRR</span>
              <span className={`text-xs font-semibold ${c.mrr > 0 ? 'text-gray-800' : 'text-gray-400'}`}>
                {c.mrr > 0 ? `$${c.mrr}/mo` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Installed</span>
              <span className="text-xs text-gray-600">{formatInstalledDate(c.installedAt, c.installedDaysAgo)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Last active</span>
              <span className="text-xs text-gray-600">{formatLastActive(c.lastActiveAt, c.lastActiveDaysAgo)}</span>
            </div>
          </div>

          <Divider />

          {/* Onboarding progress */}
          <SectionLabel>Onboarding Progress</SectionLabel>
          <div className="mb-2 space-y-1">
            {ONBOARDING_STEPS.map((step, i) => {
              const done = i <= stageIndex
              const current = i === stageIndex && step.key !== 'onboarding_complete'
              return (
                <div key={step.key} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${done ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-500' : 'bg-gray-200'}`}>
                    {done
                      ? <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      : <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    }
                  </div>
                  <span className={`text-xs font-medium ${done ? 'text-green-700' : 'text-gray-400'}`}>
                    {step.key === 'stripe_connected'
                      ? paymentConnectedStepLabel(c)
                      : step.label}
                  </span>
                  {current && (
                    <span className="ml-auto text-xs text-amber-600 font-medium">← stuck here</span>
                  )}
                </div>
              )
            })}
          </div>

          <Divider />

          {/* Match stats */}
          {c.matchStats.total > 0 && (
            <>
              <SectionLabel>Account Matching</SectionLabel>
              <div className="bg-gray-50 rounded-xl p-3 mb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">Coverage</span>
                  <span className="text-xs font-semibold text-gray-800">{matchPct(c.matchStats)}% matched</span>
                </div>
                {/* Progress bar */}
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex mb-3">
                  <div
                    className="bg-green-400 transition-all"
                    style={{ width: `${(c.matchStats.matched / c.matchStats.total) * 100}%` }}
                  />
                  <div
                    className="bg-amber-400 transition-all"
                    style={{ width: `${(c.matchStats.needsReview / c.matchStats.total) * 100}%` }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-sm font-bold text-green-600">{c.matchStats.matched}</div>
                    <div className="text-xs text-gray-400">Matched</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-amber-600">{c.matchStats.needsReview}</div>
                    <div className="text-xs text-gray-400">Review</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-500">{c.matchStats.unmatched}</div>
                    <div className="text-xs text-gray-400">Unmatched</div>
                  </div>
                </div>
              </div>
              <Divider />
            </>
          )}

          {/* Billing history */}
          <SectionLabel>Billing History</SectionLabel>
          <div className="mb-2 space-y-1">
            {(c.billingEvents || []).map((ev, i) => (
              <div key={i} className="flex items-start justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50">
                <div>
                  <p className={`text-xs font-medium ${BILLING_EVENT_STYLE[ev.type] || 'text-gray-700'}`}>{ev.event}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{ev.date}</p>
                </div>
                {ev.amount && (
                  <span className="text-xs font-semibold text-gray-700 flex-shrink-0">{ev.amount}</span>
                )}
              </div>
            ))}
          </div>

          <Divider />

          {/* Support actions */}
          <SectionLabel>Support Actions</SectionLabel>
          <div className="mb-2 space-y-2">

            {/* Extend trial */}
            {(c.subscriptionState === 'trial' || c.subscriptionState === 'trial_expired') && (
              <div className="bg-gray-50 rounded-xl p-3">
                {!extendMode ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-700">Extend Trial</p>
                      <p className="text-xs text-gray-400">Add days to their free trial</p>
                    </div>
                    <button
                      onClick={() => setExtendMode(true)}
                      className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                    >
                      Extend
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-2">Add how many days?</p>
                    <div className="flex items-center gap-2 mb-2">
                      {[7, 14, 30].map(d => (
                        <button
                          key={d}
                          onClick={() => setExtendDays(d)}
                          className={`flex-1 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                            extendDays === d ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          +{d}d
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleExtend} className="flex-1 py-1.5 text-xs bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors">
                        Confirm +{extendDays} days
                      </button>
                      <button onClick={() => setExtendMode(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mark active (for suspended / trial_expired) */}
            {(c.subscriptionState === 'suspended' || c.subscriptionState === 'trial_expired') && (
              <div className="bg-gray-50 rounded-xl p-3">
                {!confirmActivate ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-700">Mark as Active</p>
                      <p className="text-xs text-gray-400">Manually grant access (enterprise / comped)</p>
                    </div>
                    <button
                      onClick={() => setConfirmActivate(true)}
                      className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      Activate
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-1">Manually activate this org?</p>
                    <p className="text-xs text-gray-400 mb-2">This grants full access without a paid subscription.</p>
                    <div className="flex gap-2">
                      <button onClick={handleActivate} className="flex-1 py-1.5 text-xs bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors">
                        Yes, activate
                      </button>
                      <button onClick={() => setConfirmActivate(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-3">
              {!confirmReset ? (
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-700">Reset BillJean data</p>
                    <p className="text-xs text-gray-400">Delete this customer's BillJean setup so they start again</p>
                  </div>
                  <button
                    onClick={() => setConfirmReset(true)}
                    className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex-shrink-0"
                  >
                    Reset
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Reset BillJean data for this customer?</p>
                  <p className="text-xs text-gray-400 mb-2">
                    {isPipedrive
                      ? 'This deletes this Pipedrive company’s BillJean data the same way uninstall does. They leave this list until they install BillJean again.'
                      : isGhl
                      ? 'This deletes their agents, matches, and payment connection. Setup starts again. Their HighLevel trial and subscription stay as they are.'
                      : 'This permanently deletes BillJean data for this Salesforce org. They leave this list until they open BillJean and start setup again.'}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleReset}
                      disabled={resetting}
                      className="flex-1 py-1.5 text-xs bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-60"
                    >
                      {resetting ? 'Resetting...' : 'Yes, reset'}
                    </button>
                    <button
                      onClick={() => setConfirmReset(false)}
                      disabled={resetting}
                      className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>

          <Divider />

          {/* Notes */}
          <SectionLabel>Internal Notes</SectionLabel>
          <div className="mb-4">
            <textarea
              value={notes}
              onChange={e => onNotesChange(e.target.value)}
              placeholder="Add notes about this account (visible to your team only)..."
              rows={4}
              className="w-full text-xs border border-gray-200 rounded-xl p-3 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-gray-300 text-gray-700"
            />
            <div className="flex items-center justify-end mt-1.5">
              {noteSaved && <span className="text-xs text-green-600 mr-2">Saved</span>}
              <button
                onClick={handleSaveNote}
                className="text-xs px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Save note
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  )
}

// ── Info row helper ───────────────────────────────────────────────────────────

function InfoRow({ icon, label, value, mono, muted }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
      <span className={`text-xs text-right truncate max-w-[220px] ${
        muted ? 'text-gray-400' : mono ? 'text-gray-700 font-mono' : 'text-gray-700 font-medium'
      }`}>
        {value}
      </span>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

const STATE_SORT_ORDER = { active: 1, trial: 2, payment_failed: 3, trial_expired: 4, suspended: 5, cancelled: 6 }

export default function OperatorAdmin() {
  const { operator } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const createRoute = location.pathname === '/operator/create'
  const initialView = createRoute || searchParams.get('view') === 'operators' ? 'operators' : 'customers'
  const [activeView, setActiveView] = useState(initialView) // 'customers' or 'operators'
  const [customers, setCustomers] = useState([])
  const [metrics, setMetrics] = useState({ monthly_revenue: 0, active_seats: 0, active_subscribers: 0, total_seats: 0, in_trial: 0, at_risk: 0 })
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const selectedIdRef = useRef(null)
  selectedIdRef.current = selectedId
  const [sortBy, setSortBy] = useState('mrr')
  const [sortDir, setSortDir] = useState('desc')
  const [notes, setNotes] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [counts, setCounts] = useState({ all: 0, trial: 0, active: 0, at_risk: 0, suspended: 0, cancelled: 0 })
  const [currentPage, setCurrentPage] = useState(PAGINATION_DEFAULTS.PAGE)
  const [perPage, setPerPage] = useState(PAGINATION_DEFAULTS.PER_PAGE)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  // Load metrics and customers on mount and when filters change.
  // A quiet refresh follows the one-minute sync so CRM changes show up on this page.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadData()
    }, search ? 500 : 0)

    const refreshId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadData({ silent: true })
      }
    }, 60000)

    return () => {
      clearTimeout(timeoutId)
      clearInterval(refreshId)
    }
  }, [filter, search, sortBy, sortDir, currentPage, perPage])

  async function loadData({ silent = false } = {}) {
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      const [metricsData, customersData] = await Promise.all([
        operatorApi.getMetrics({ silent }),
        operatorApi.getCustomers({
          filter,
          search,
          sort_by: sortBy,
          sort_dir: sortDir,
          page: currentPage,
          per_page: perPage,
        }, { silent }),
      ])

      const normalizedCustomers = (Array.isArray(customersData.customers)
        ? customersData.customers
        : Object.values(customersData.customers || {})
      ).map(normalizeOperatorCustomer)

      setMetrics(metricsData)
      setCustomers(normalizedCustomers)
      setCounts(customersData.counts || {})
      const pagination = customersData.pagination || {}
      setTotalPages(pagination.last_page ?? customersData.total_pages ?? 1)
      setTotalItems(pagination.total ?? customersData.total ?? normalizedCustomers.length)
      if (pagination.current_page && pagination.current_page !== currentPage) {
        setCurrentPage(pagination.current_page)
      }

      setNotes((prev) => {
        const notesMap = {}
        const openId = selectedIdRef.current
        normalizedCustomers.forEach((c) => {
          notesMap[c.id] = silent && c.id === openId && prev[c.id] !== undefined
            ? prev[c.id]
            : (c.notes || '')
        })
        return notesMap
      })
      if (silent && selectedIdRef.current && !normalizedCustomers.some((c) => c.id === selectedIdRef.current)) {
        setSelectedId(null)
      }
    } catch (err) {
      if (!silent) {
        setError(err?.message ?? 'Failed to load data')
      }
      console.error('Failed to load data:', err)
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }

  // Customers are already filtered and sorted by the API
  const filtered = customers

  function handleSort(field) {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortDir('desc')
    }
    setCurrentPage(1)
  }

  const selectedCustomer = selectedId ? customers.find(c => c.id === selectedId) : null

  async function handleExtendTrial(days) {
    if (!selectedId) return
    try {
      await operatorApi.extendTrial(selectedId, days)
      await loadData()
    } catch (err) {
      console.error('Failed to extend trial:', err)
      // Toast shown by operatorApi
    }
  }

  async function handleMarkActive() {
    if (!selectedId) return
    try {
      await operatorApi.markActive(selectedId)
      await loadData()
    } catch (err) {
      console.error('Failed to mark as active:', err)
      // Toast shown by operatorApi
    }
  }

  async function handleResetCustomer() {
    if (!selectedId) return
    const result = await operatorApi.resetCustomer(selectedId)
    if (result?.removed) {
      setSelectedId(null)
    }
    await loadData()
  }

  async function handleSaveNote() {
    if (!selectedId) return
    try {
      await operatorApi.updateNotes(selectedId, notes[selectedId] || '')
      await loadData()
    } catch (err) {
      console.error('Failed to save note:', err)
      // Toast shown by operatorApi
    }
  }

  return (
    <div className="min-h-full bg-gray-50">

      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-semibold text-gray-900">Operator Panel</h1>
                <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">INTERNAL</span>
              </div>
              <p className="text-xs text-gray-400">
                {activeView === 'customers' ? `All customer orgs · ${totalItems} total` : 'Manage BillJean team members'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* User Profile with dropdown */}
            <UserProfile />
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-1 mt-4 border-b border-gray-200">
          <button
            onClick={() => {
              if (createRoute) {
                navigate('/operator', { replace: true })
                return
              }
              setActiveView('customers')
              setSearchParams({}, { replace: true })
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              activeView === 'customers'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Customers
          </button>
          <button
            onClick={() => {
              if (createRoute) {
                navigate('/operator?view=operators', { replace: true })
                return
              }
              setActiveView('operators')
              setSearchParams({ view: 'operators' }, { replace: true })
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              activeView === 'operators'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Operator Management
          </button>
        </div>
      </div>

      <div className="p-6">
        {activeView === 'customers' ? (
          <div className="space-y-5">
            {loading ? (
              <LoadingSpinner fullScreen text="Loading customers..." />
            ) : error ? (
              <ErrorState message={error} onRetry={loadData} />
            ) : (
              <>
            {/* Metric cards */}
            <div className="grid grid-cols-4 gap-4">
              <MetricCard
                label="Monthly Revenue"
                value={`$${metrics.monthly_revenue || 0}`}
                sub={`${metrics.active_subscribers || 0} active subscribers • ${metrics.active_seats || 0} seats`}
                accent="green"
              />
              <MetricCard
                label="Active Seats"
                value={metrics.active_seats || 0}
                sub="billable agents"
                accent="blue"
              />
              <MetricCard
                label="In Trial"
                value={metrics.in_trial || 0}
                sub="free trial orgs"
                accent="indigo"
              />
              <MetricCard
                label="At Risk"
                value={metrics.at_risk || 0}
                sub="need attention"
                accent="red"
                alert={(metrics.at_risk || 0) > 0}
              />
            </div>

            {/* Filter + Search */}
            <div className="flex items-center justify-between gap-4">
              <FilterTabs
                filter={filter}
                onChange={(nextFilter) => {
                  setFilter(nextFilter)
                  setCurrentPage(1)
                }}
                counts={counts}
              />
              <div className="relative">
                <svg className="absolute left-2.5 top-2 text-gray-400 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search orgs, admins..."
                  value={search}
                  onChange={e => {
                    setSearch(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-52"
                />
              </div>
            </div>

            {/* Customer table */}
            <CustomerTable
              customers={filtered}
              selectedId={selectedId}
              onSelect={id => setSelectedId(selectedId === id ? null : id)}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={handleSort}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPerPageChange={(v) => { setPerPage(v); setCurrentPage(1) }}
            />
              </>
            )}
          </div>
        ) : (
          <OperatorManagement />
        )}
      </div>

      {/* Detail drawer */}
      {selectedCustomer && (
        <CustomerDrawer
          customer={selectedCustomer}
          notes={notes[selectedCustomer.id] || ''}
          onNotesChange={v => setNotes(prev => ({ ...prev, [selectedCustomer.id]: v }))}
          onSaveNote={handleSaveNote}
          onClose={() => setSelectedId(null)}
          onExtendTrial={handleExtendTrial}
          onMarkActive={handleMarkActive}
          onReset={handleResetCustomer}
        />
      )}

    </div>
  )
}
